import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, TextField, Button, Typography, Box, Avatar
} from '@mui/material';
import MDButton from 'components/MDButton';
import * as yup from 'yup';
import Swal from 'sweetalert2';
import api from 'core/interceptors/api';
import { useFormik } from 'formik';

const MAX_FILE_SIZE = 2 * 1024 * 1024;

const validationBusinessCategorySchema = yup.object().shape({
  business_category: yup.string()
    .required('Business Category is required')
    .min(3, 'Business Category must be at least 3 characters.')
    .max(50, 'Business Category must not exceed 50 characters.')
    .matches(
      /^[A-Za-z\s]+$/,
      'Business Category must only contain letters and spaces.'
    ),
});

export default function BusinessCategoryAddModel({ open, handleClose }) {
  const [duplicateError, setDuplicateError] = useState('');
  const [image, setImage] = useState(null);
  const [imageError, setImageError] = useState('');
  const [imagePreview, setImagePreview] = useState(null);
  const [isImageSelectedAndValid, setIsImageSelectedAndValid] = useState(false);

  const checkDuplicate = async (value) => {
    const trimmed = value.trim();
    if (!trimmed || !/^[A-Za-z\s]+$/.test(trimmed)) {
      setDuplicateError('');
      return;
    }
    try {
      const response = await api.post('/vendor_management/business_category/check_duplicate', {
        business_category: trimmed,
      });
      setDuplicateError(response.data.status === 1 ? 'Business Category already exists' : '');
    } catch (error) {
      setDuplicateError('Error checking duplicate');
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        setImageError('Image size must be less than 2MB.');
        setImage(null);
        setImagePreview(null);
        setIsImageSelectedAndValid(false);
      } else {
        setImage(file);
        setImageError('');
        setImagePreview(URL.createObjectURL(file));
        setIsImageSelectedAndValid(true);
      }
    } else {
      setImage(null);
      setImageError('');
      setImagePreview(null);
      setIsImageSelectedAndValid(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      business_category: '',
    },
    validationSchema: validationBusinessCategorySchema,
    onSubmit: async (values) => {
      await formik.validateForm();
      if (!formik.isValid || duplicateError || imageError || !isImageSelectedAndValid) {
        return;
      }

      try {
        const formData = new FormData();
        formData.append('business_category', values.business_category.trim());
        if (image) {
          formData.append('image', image);
        }

        const response = await api.post('/vendor_management/business_category/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        if (response.data.status === 1) {
          handleClose({
            id: response.data.id,
            business_category_name: values.business_category,
          });

          Swal.fire({
            icon: 'success',
            title: 'Business Category Added Successfully!',
            text: response.data.msg,
            timer: 3000,
            showConfirmButton: false,
          });

          formik.resetForm();
          setImage(null);
          setImagePreview(null);
          setImageError('');
          setDuplicateError('');
          setIsImageSelectedAndValid(false);
        } else {
          Swal.fire({ icon: 'error', title: 'Error', text: response.data.msg });
        }
      } catch (error) {
        Swal.fire({ icon: 'error', title: 'Error', text: 'Upload failed' });
      }
    },
  });

  return (
    <Dialog open={open} onClose={() => {
      formik.resetForm();
      formik.setErrors({});
      formik.setTouched({});
      setImage(null);
      setImageError('');
      setImagePreview(null);
      setDuplicateError('');
      setIsImageSelectedAndValid(false);
      handleClose();
    }} fullWidth maxWidth="sm">
      <DialogTitle>Add Business Category</DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Business Category"
                name="business_category"
                value={formik.values.business_category}
                onChange={async (e) => {
                  formik.handleChange(e);
                  formik.setFieldTouched('business_category', true, false);
                  await formik.validateField('business_category');

                  if (validationBusinessCategorySchema.fields.business_category.isValidSync(e.target.value)) {
                    checkDuplicate(e.target.value);
                  } else {
                    setDuplicateError('');
                  }
                }}
                onBlur={formik.handleBlur}
                fullWidth
                margin="normal"
                error={
                  (formik.touched.business_category && Boolean(formik.errors.business_category)) ||
                  Boolean(duplicateError)
                }
                helperText={
                  (formik.touched.business_category && formik.errors.business_category) ||
                  duplicateError
                }
              />
            </Grid>

            <Grid item xs={12}>
              <MDButton
                variant="outlined"
                color="info"
                component="label"
                fullWidth
                sx={{ justifyContent: 'center' }}
              >
                Upload Image (Max 2MB)
                <input
                  hidden
                  accept="image/*"
                  type="file"
                  onChange={handleImageChange}
                  onClick={(event) => { event.target.value = null; }}
                />
              </MDButton>

              {imagePreview && (
                <Box mt={2} textAlign="center">
                  <Avatar
                    src={imagePreview}
                    alt="Preview"
                    variant="rounded"
                    sx={{ width: 100, height: 100, mx: 'auto' }}
                  />
                </Box>
              )}
              {imageError && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  {imageError}
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              formik.resetForm();
              formik.setErrors({});
              formik.setTouched({});
              setImage(null);
              setImageError('');
              setImagePreview(null);
              setDuplicateError('');
              setIsImageSelectedAndValid(false);
              handleClose();
            }}
          >
            Cancel
          </Button>
          <MDButton
            type="submit"
            color="success"
            disabled={
              !formik.isValid ||
              !formik.dirty ||
              Boolean(duplicateError) ||
              Boolean(imageError) ||
              !isImageSelectedAndValid
            }
          >
            Save
          </MDButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}